import { nanoid, type Store } from '@blocksuite/affine/store';
import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';

import * as styles from './object-annotation.css';
import { type AnnotationChatMessage } from './object-annotation-store';

const STATUSES = ['none', 'Idea', 'Plan', 'Implement', 'Test', 'Monitor'];
export const AI_ITERATIONS_KEY = 'mpxAiIterations';

export function ObjectWorkflow({
  parent,
  annotation,
  readonly,
}: {
  parent: Store;
  annotation: Y.Map<unknown>;
  readonly: boolean;
}) {
  const [status, setStatus] = useState('none');
  const [armed, setArmed] = useState(false);
  const [messages, setMessages] = useState<[string, AnnotationChatMessage][]>(
    []
  );
  const [run, setRun] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const disabled = readonly || parent.readonly;
  useEffect(() => {
    const update = () => {
      setStatus(String(annotation.get('status') ?? 'none'));
      setArmed(annotation.get('aiArmed') === true);
      const chat = annotation.get('chat');
      setMessages(
        chat instanceof Y.Map
          ? [...chat.entries()].sort(
              ([a, x], [b, y]) =>
                x.time.localeCompare(y.time) || a.localeCompare(b)
            )
          : []
      );
      const iterationId = String(annotation.get('aiIteration') ?? '');
      const registry = parent.root?.yBlock.get(AI_ITERATIONS_KEY);
      const iteration =
        registry instanceof Y.Map ? registry.get(iterationId) : null;
      setRun(
        iterationId ? iterationId + ' · ' + (iteration?.state ?? 'queued') : ''
      );
    };
    update();
    annotation.observeDeep(update);
    parent.spaceDoc.on('afterTransaction', update);
    return () => {
      annotation.unobserveDeep(update);
      parent.spaceDoc.off('afterTransaction', update);
    };
  }, [annotation, parent]);
  useEffect(() => {
    if (open) dialog.current?.showModal();
  }, [open]);
  useEffect(() => {
    if (open) end.current?.scrollIntoView({ block: 'nearest' });
  }, [open, messages]);
  const edit = (fn: () => void) => {
    if (!disabled) {
      parent.captureSync();
      parent.transact(fn);
    }
  };
  return (
    <section className={styles.workflow}>
      <label className={styles.statusLabel}>
        Status
        <select
          aria-label="Status"
          disabled={disabled}
          value={status}
          onChange={event =>
            edit(() => annotation.set('status', event.target.value))
          }
        >
          {STATUSES.map(value => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      <label className={styles.armLabel}>
        <button
          type="button"
          role="checkbox"
          className={styles.checkbox}
          aria-label="AI ARM"
          aria-checked={armed}
          disabled={disabled}
          onClick={() => edit(() => annotation.set('aiArmed', !armed))}
        >
          {armed ? '✓' : ''}
        </button>
        AI ARM
      </label>
      <button className={styles.smallButton} onClick={() => setOpen(true)}>
        AI Chat{messages.length ? ' (' + messages.length + ')' : ''}
      </button>
      {open && (
        <dialog
          ref={dialog}
          className={styles.chatDialog}
          aria-label="Object AI Chat"
          onCancel={() => setOpen(false)}
          onClose={() => setOpen(false)}
        >
          <header className={styles.chatHeader}>
            <strong>AI Chat</strong>
            <button
              className={styles.iconButton}
              aria-label="Close AI Chat"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>
          <p className={styles.chatHint}>
            Messages are saved with this object. Arm it and use Run AI to queue
            the conversation. Replies arrive when a connected AI runner
            processes the iteration.
          </p>
          {run && (
            <p role="status" className={styles.chatHint}>
              Iteration {run}
            </p>
          )}
          <div className={styles.chatMessages} aria-live="polite">
            {messages.length ? (
              messages.map(([id, message]) => (
                <article
                  key={id}
                  className={styles.chatMessage}
                  data-role={message.role}
                >
                  <strong>
                    {message.role === 'user'
                      ? 'You'
                      : message.role === 'assistant'
                        ? 'AI'
                        : 'Iteration'}
                  </strong>
                  <p>{message.content}</p>
                  <time dateTime={message.time}>
                    {new Date(message.time).toLocaleString()}
                  </time>
                </article>
              ))
            ) : (
              <p className={styles.kind}>No messages yet.</p>
            )}
            <div ref={end} />
          </div>
          <form
            className={styles.chatComposer}
            onSubmit={event => {
              event.preventDefault();
              const content = draft.trim();
              const chat = annotation.get('chat');
              if (!content || disabled || !(chat instanceof Y.Map)) return;
              edit(() =>
                chat.set(nanoid(), {
                  role: 'user',
                  content,
                  time: new Date().toISOString(),
                } satisfies AnnotationChatMessage)
              );
              setDraft('');
            }}
          >
            <textarea
              autoFocus
              aria-label="Chat message"
              placeholder="Write a message for the next AI iteration…"
              value={draft}
              disabled={disabled}
              onChange={event => setDraft(event.target.value)}
            />
            <button
              className={styles.saveButton}
              disabled={disabled || !draft.trim()}
            >
              Save message
            </button>
          </form>
        </dialog>
      )}
    </section>
  );
}

export { AIRunManager as GlobalAnnotationAI } from './ai-run-manager';
